import JSON5 from 'json5';
import { Answer, toDatabaseUserType } from './api';
import { FeedbackStatus } from '../api';
import {
  FeedbackCommentBodyWithIndex,
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import { FeedbackType } from 'npo/pages/NPOFeedback/SubmitReady/utils';
import { getStaticUserType } from 'shared/auth/hooks/useUserActions';
import { ImpactReceipt } from '../../DonorReceipt/utils';
import { investmentQuestions } from 'npo/pages/Tasks/Allocation/questions';

export type UserType = 'ti staff' | 'donor' | 'npo' | 'cn-npo';

export type FeedbackSectionId =
  | 'allocation'
  | 'custom_questions'
  | 'beneficiaries'
  | 'budget'
  | 'dates'
  | 'description'
  | 'activities'
  | 'impact_summary'
  | 'indicator_details'
  | 'location'
  | 'narrative'
  | 'finalize_report'
  | 'interim_report';

export interface FeedbackSection {
  label: string;
  sectionId: FeedbackSectionId;
}

export type DatabaseUserType = 'grantee' | 'admin' | 'funder';

export interface User {
  email: string;
  email_verified?: boolean; // mostly true
  family_name?: string;
  given_name?: string;
  familyName?: string;
  givenName?: string;
  sub: string;
  'custom:account_id': string; // "1" for admin
  'custom:created_at': string;
  'custom:title': string;
  'custom:type': DatabaseUserType;
  'custom:user_id': string;
}

export const getCurrentUserFullName = (user?: User) =>
  `${user?.given_name || user?.givenName} ${user?.family_name ||
    user?.familyName}`;

export interface Contact {
  name: string;
  email?: string;
  accountType?: UserType;
}

export interface FeedbackCommentProps {
  userId: number;
  details?: string;
  commentId: number;
  text: string;
  name: Contact['name'];
  createdAt: string;
  updatedAt?: string;
  email?: Contact['email'];
  accountType?: Contact['accountType'];
}

export const toFeedbackCommentBody = (
  questionId: number,
  reportId: number,
  sectionId: FeedbackSectionId,
  comment: FeedbackCommentProps[],
): FeedbackCommentBodyWithIndex[] => {
  const result: FeedbackCommentBodyWithIndex[] = [];
  for (let i = 0; i < comment.length; i++) {
    result.push({
      commentId: comment[i].commentId,
      userName: comment[i].name,
      userId: comment[i].userId,
      userEmail: comment[i].email || '',
      details: comment[i].details,
      comment: comment[i].text,
      userType: toDatabaseUserType(comment[i].accountType || 'ti staff'),
      questionId: questionId,
      reportId: reportId,
      sectionId: sectionId,
      createdAt: comment[i].createdAt,
      index: i,
    });
  }
  return result;
};

interface Template {
  name: string;
  description: string;
  createdAt: string;
  userName: string;
}

interface TemplateSection {
  name: string;
  questionId: number;
  templates: Template[];
}

export interface AllocationAnswer {
  questionId: number;
  questionText: string;
  text: string | string[];
  ignoreClearData: boolean;
}

export type SectionLevelComments = {
  [sectionId in FeedbackSectionId]+?: FeedbackCommentProps[] | undefined;
};

export type SectionFeedbackTemplates = {
  [sectionId in FeedbackSectionId]+?: TemplateSection[] | undefined;
};

export const userTypeToString = (type?: UserType): string => {
  switch (type) {
    case 'donor':
      return 'Donor';
    case 'npo':
      return 'NPO';
    case 'ti staff':
      return 'True Impact Staff';

    default:
      return 'Unknown';
  }
};

export const FeedbackSectionIds: FeedbackSectionId[] = [
  'description',
  'dates',
  // 'allocation',
  'location',
  'activities',
  'impact_summary',
  'indicator_details',
  'beneficiaries',
  'narrative',
  'budget',
];

export const parseString = (o: string): object | undefined => {
  if (typeof o !== 'string') return undefined;
  try {
    return JSON5.parse(o);
  } catch (e) {
    return undefined;
  }
};

const getQuestionId = (x: Answer | AllocationAnswer): number => {
  return x.questionId || (x as Answer).question.dbId;
};

const isAllocationCompleted = (allocations: (Answer | AllocationAnswer)[]) => {
  const firstQuestion = allocations.find(x => getQuestionId(x) === 20008)?.text;
  const secondQuestion = allocations.find(x => getQuestionId(x) === 20016)
    ?.text;
  const thirdQuestion = allocations.find(x => getQuestionId(x) === 20009)?.text;
  if (!firstQuestion || !thirdQuestion) return false;
  if (
    firstQuestion === investmentQuestions[20008]?.options[1].optionText &&
    !secondQuestion
  ) {
    return false;
  }
  return true;
};

export const isSignificantInvestmentAnswer = (
  a: Answer | AllocationAnswer | { text: string },
): boolean => a.text.includes('but important');
const isSignificantInvestment = (
  allocations: (Answer | AllocationAnswer | { text: string })[],
): boolean => {
  return allocations.some(isSignificantInvestmentAnswer);
};

export const getInvestmentType = (
  allocations: (Answer | AllocationAnswer)[] | undefined,
): ImpactReceipt['allocation']['tier'] | 'Unknown' => {
  if (
    !allocations ||
    allocations.length === 0 ||
    !isAllocationCompleted(allocations)
  ) {
    return 'Unknown';
  }
  if (isSignificantInvestment(allocations)) return 'Significant';
  return 'Foundational';
};

const getAnswersArray = (obj: Record<string, unknown>) => {
  const result: { id: string; value: string | number }[] = [];
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const id = keys[i];
    const value = obj[id];
    if (typeof value === 'string' || typeof value === 'number') {
      result.push({ id, value });
    } else {
      if (
        Array.isArray(value) &&
        value.join &&
        (value.length === 0 ||
          typeof value[0] !== 'object' ||
          value[0] === null)
      ) {
        result.push({ id, value: value.join() });
      } else if (value && typeof value === 'object') {
        const answers = getAnswersArray(value as Record<string, unknown>);
        answers.forEach(a => result.push({ id: a.id, value: a.value }));
      }
    }
  }
  return result;
};

export const renderAnsweredQuestions = (
  text: string,
): string | { id: string; value: string | number }[] => {
  try {
    const parsedText = JSON5.parse(text);
    if (parsedText[0] && typeof parsedText[0] === 'string') {
      return parsedText.join(', ');
    }

    if (parsedText && typeof parsedText === 'object') {
      return getAnswersArray(parsedText as Record<string, unknown>);
    }

    return text;
  } catch (error) {
    return text;
  }
};

export const isReportReadyForUser = (
  status: FeedbackStatus,
  type: UserType,
) => {
  switch (status) {
    case 'Awaiting TI Feedback':
    case 'Awaiting TI Verification':
    case 'Awaiting Funder Feedback':
    case 'Awaiting Funder Verification':
      if (type === 'donor' || type === 'ti staff') return true;
      return false;

    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      if (type === 'npo') return true;
      return false;

    default:
      return false;
  }
};

export interface FeedbackQuestionTitleRenderProps {
  title: string;
  disabled?: boolean;
  commentsLength: number;
}

export type FeedbackComments = {
  [questionId: number]: FeedbackCommentProps[] | undefined;
};

export interface RenderQuestionProps extends FeedbackQuestionTitleRenderProps {
  answer: Answer;
  reportId: number;
  questionId: number;
  className?: string;
  currentUserId: string | undefined;
  currentUserEmail: string | undefined;
  currentUserName: string;
  currentUserType: UserType;
  comments: FeedbackCommentProps[];
  handleOpen: () => unknown;
  addOrUpdateComment: (
    questionId:
      | typeof INDICATOR_DETAILS_QUESTION_ID
      | typeof IMPACT_SUMMARY_QUESTION_ID,
    comment: FeedbackCommentProps,
    index?: number,
  ) => unknown;
}

export const getFeedbackView = (
  status: FeedbackStatus,
  feedbackType: FeedbackType,
  userType: UserType = getStaticUserType(),
): 'verification' | 'feedback' | 'awaiting' => {
  switch (userType) {
    case 'npo':
      switch (feedbackType) {
        case 'TI Only':
        case 'Donor Only':
        case 'No Feedback':
        case 'Full (TI + Donor)':
          switch (status) {
            case 'TI Feedback Provided':
            case 'Funder Feedback Provided':
              return 'verification';
            case 'Draft':
            case 'Interim Updates Needed':
            case 'Final Updates Needed':
            case 'Awaiting Funder Feedback':
            case 'Awaiting TI Feedback':
            case 'Awaiting TI Verification':
            case 'Awaiting Funder Verification':
            case 'Published':
            case 'Finalized':
              return 'awaiting';
          }
      }
      // falls through
    case 'donor':
      switch (feedbackType) {
        case 'TI Only':
        case 'Donor Only':
        case 'No Feedback':
        case 'Full (TI + Donor)':
          switch (status) {
            case 'Draft':
            case 'Interim Updates Needed':
            case 'Final Updates Needed':
            case 'Published':
            case 'Finalized':
            case 'Awaiting TI Feedback':
            case 'Funder Feedback Provided':
            case 'Awaiting TI Verification':
              return 'awaiting';
            case 'TI Feedback Provided':
            case 'Awaiting Funder Feedback':
              return 'feedback';
            case 'Awaiting Funder Verification':
              return 'verification';
          }
      }
      // falls through
    case 'ti staff':
      switch (feedbackType) {
        case 'TI Only':
        case 'Donor Only':
        case 'No Feedback':
        case 'Full (TI + Donor)':
          switch (status) {
            case 'Draft':
            case 'Interim Updates Needed':
            case 'Final Updates Needed':
            case 'Published':
            case 'Finalized':
            case 'TI Feedback Provided':
            case 'Funder Feedback Provided':
            case 'Awaiting Funder Feedback':
            case 'Awaiting Funder Verification':
              return 'awaiting';
            case 'Awaiting TI Feedback':
              return 'feedback';
            case 'Awaiting TI Verification':
              return 'verification';
          }
      }
  }

  return 'awaiting';
};
